import highRiskZoneEndpoints from './endpoints/highRiskZone.endpoints';

/**
 * Get zone visit statistics for a subject.
 * @param {number} alienId - The alien/subject identifier.
 * @param {number} [days=30] - Number of days to analyze.
 * @returns {Promise<Object|null>} Zone visit statistics with exposure score.
 */
export async function getZoneVisitStats(alienId, days = 30) {
  try {
    const response = await highRiskZoneEndpoints.getZoneVisitStats(alienId, { params: { days } });
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    if (error.response?.status === 404) {
      console.log(`[getZoneVisitStats] No zone visit stats found for alien ${alienId}`);
      return null;
    }
    console.error('[getZoneVisitStats] Error:', error);
    return null;
  }
}

/**
 * Get zone visit history for a subject.
 * @param {number} alienId - The alien/subject identifier.
 * @param {number} [days=30] - Number of days to include.
 * @returns {Promise<Object|null>} Zone visit history with individual visits.
 */
export async function getZoneVisitHistory(alienId, days = 30) {
  try {
    const response = await highRiskZoneEndpoints.getZoneVisitHistory(alienId, { params: { days } });
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    if (error.response?.status === 404) {
      console.log(`[getZoneVisitHistory] No zone visit history found for alien ${alienId}`);
      return null;
    }
    console.error('[getZoneVisitHistory] Error:', error);
    return null;
  }
}

/**
 * Get the zone exposure score for ML risk scoring.
 * @param {number} alienId - The alien/subject identifier.
 * @param {number} [days=30] - Number of days to analyze.
 * @returns {Promise<Object|null>} Zone exposure score data.
 */
export async function getZoneExposureScore(alienId, days = 30) {
  try {
    const response = await highRiskZoneEndpoints.getZoneExposureScore(alienId, { params: { days } });
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    if (error.response?.status === 404) {
      console.log(`[getZoneExposureScore] No exposure score found for alien ${alienId}`);
      return null;
    }
    console.error('[getZoneExposureScore] Error:', error);
    return null;
  }
}

/**
 * Get all active high-risk zones.
 * @param {boolean} [includeExpired=false] - Include expired zones.
 * @returns {Promise<Array|null>} List of active high-risk zones.
 */
export async function getActiveZones(includeExpired = false) {
  try {
    const response = await highRiskZoneEndpoints.getActiveZones({ params: { includeExpired } });
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error('[getActiveZones] Error:', error);
    return null;
  }
}

/**
 * Get a specific high-risk zone by ID.
 * @param {string} zoneId - The zone identifier.
 * @returns {Promise<Object|null>} The zone details.
 */
export async function getZoneById(zoneId) {
  try {
    const response = await highRiskZoneEndpoints.getZoneById(zoneId);
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    if (error.response?.status === 404) {
      console.log(`[getZoneById] Zone ${zoneId} not found`);
      return null;
    }
    console.error('[getZoneById] Error:', error);
    return null;
  }
}

/**
 * Check if a location is within any high-risk zones.
 * @param {number} latitude - Latitude to check.
 * @param {number} longitude - Longitude to check.
 * @param {string} [participantId] - Optional participant ID for recording.
 * @returns {Promise<Object|null>} Zone check result with matching zones.
 */
export async function checkPointInZones(latitude, longitude, participantId = null) {
  try {
    const payload = { latitude, longitude };
    if (participantId) {
      payload.participantId = participantId;
    }

    const response = await highRiskZoneEndpoints.checkPointInZones(payload);
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error('[checkPointInZones] Error:', error);
    return null;
  }
}

/**
 * Format dwell time for display.
 * @param {number} seconds - Dwell time in seconds.
 * @returns {string} Formatted dwell time string.
 */
export function formatDwellTime(seconds) {
  if (!seconds || seconds <= 0) return '0s';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }
  if (minutes > 0) {
    return secs > 0 ? `${minutes}m ${secs}s` : `${minutes}m`;
  }
  return `${secs}s`;
}

/**
 * Get risk level color for display.
 * @param {number} riskLevel - Risk level 1-5.
 * @returns {string} Color string for the risk level.
 */
export function getRiskLevelColor(riskLevel) {
  const colors = {
    1: '#4caf50', // green
    2: '#8bc34a', // light green
    3: '#ffeb3b', // yellow
    4: '#ff9800', // orange
    5: '#f44336', // red
  };
  return colors[riskLevel] || '#9e9e9e';
}

/**
 * Get risk level label for display.
 * @param {number} riskLevel - Risk level 1-5.
 * @returns {string} Label for the risk level.
 */
export function getRiskLevelLabel(riskLevel) {
  const labels = {
    1: 'Low',
    2: 'Moderate',
    3: 'Elevated',
    4: 'High',
    5: 'Critical',
  };
  return labels[riskLevel] || 'Unknown';
}

/**
 * Get zone category display label.
 * @param {string} category - Zone category code.
 * @returns {string} Human-readable category label.
 */
export function getZoneCategoryLabel(category) {
  const labels = {
    'known_stash_house': 'Stash House',
    'gang_territory': 'Gang Territory',
    'smuggling_corridor': 'Smuggling Corridor',
    'previous_arrest_location': 'Previous Arrest Location',
    'associated_criminal_address': 'Criminal Associate Address',
    'border_crossing_point': 'Border Crossing',
    'flight_risk_location': 'Flight Risk Location',
    'monitored_residence': 'Monitored Residence',
  };
  return labels[category] || category?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown';
}
